import { useApi } from '@/api';
import {
  Flex,
  formatPercentage,
  textShortener,
  DoughnutChart,
  ChartSkeleton,
  EmptyBox
} from '@blockanalitica/ui';
import { useChartTooltip } from '@/hooks';
import { useParams, useNavigate } from 'react-router';
import _ from 'lodash';
import type { DoughnutChartData } from '@blockanalitica/ui/package/types/src/components/charts/DoughnutChart/DoughnutChart';

type ShareData = {
  wallet_address: string;
  balance: string;
  percent_of_total: string;
};

export default function SuppliersDonutChart() {
  const { marketAddress, collateralAddress } = useParams();
  const navigate = useNavigate();
  const [data, error] = useApi<ShareData[]>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/share/`
  });

  const customTooltip = useChartTooltip(
    data,
    'wallet_address',
    'percent_of_total',
    true,
    'balance'
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" alignItems="center" paddingTop="m">
        <ChartSkeleton type="doughnut" />
      </Flex>
    );
  }

  if (_.isEmpty(data)) {
    return <EmptyBox />;
  }

  const chartData: DoughnutChartData = {
    labels: data.map((row) => textShortener(row.wallet_address)),
    datasets: [
      {
        data: data.map((row) => Number(row.percent_of_total))
      }
    ]
  };
  const mapping: Record<string, string> = data.reduce(
    (acc: Record<string, string>, row) => {
      if (row.wallet_address === 'others') {
        acc['others'] = `../${collateralAddress}/wallets`;
      } else {
        acc[textShortener(row.wallet_address)] = `${row.wallet_address}`;
      }
      return acc;
    },
    {}
  );

  const customConfig = {
    options: {
      aspectRatio: 2.4,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: customTooltip
        }
      }
    }
  };

  return (
    <Flex width="100%">
      <DoughnutChart
        config={customConfig}
        data={chartData}
        valueFormatter={formatPercentage}
        navigate={(path) => navigate(path, { relative: 'path' })}
        url={`../borrowers`}
        mapping={mapping}
      />
    </Flex>
  );
}
